// Enhanced caching system with LRU and TTL for route data
import type { CacheEntry, CacheStats } from "../types/routes";

// Enhanced cache with LRU and TTL
export class RouteCache<T = any> {
  private cache = new Map<string, CacheEntry<T>>();
  private accessOrder = new Map<string, number>();
  private maxSize: number;
  private defaultTTL: number;
  private accessCounter = 0;
  private stats = { hits: 0, misses: 0 };

  constructor(maxSize = 100, defaultTTL = 30 * 60 * 1000) {
    // 30 min TTL
    this.maxSize = maxSize;
    this.defaultTTL = defaultTTL;
  }

  get(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) {
      this.stats.misses++;
      return null;
    }

    // Check TTL
    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      this.accessOrder.delete(key);
      this.stats.misses++;
      return null;
    }

    // Update access order for LRU
    this.accessOrder.set(key, ++this.accessCounter);
    this.stats.hits++;
    return entry.data;
  }

  set(key: string, data: T, ttl?: number): void {
    // Remove oldest entries if at capacity
    if (this.cache.size >= this.maxSize) {
      const oldestKey = this.findOldestKey();
      if (oldestKey) {
        this.cache.delete(oldestKey);
        this.accessOrder.delete(oldestKey);
      }
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.defaultTTL,
    });
    this.accessOrder.set(key, ++this.accessCounter);
  }

  private findOldestKey(): string | null {
    let oldestKey: string | null = null;
    let oldestAccess = Infinity;

    // Use Array.from for compatibility with older TypeScript targets
    Array.from(this.accessOrder.entries()).forEach(([key, access]) => {
      if (access < oldestAccess) {
        oldestAccess = access;
        oldestKey = key;
      }
    });

    return oldestKey;
  }

  clear(): void {
    this.cache.clear();
    this.accessOrder.clear();
    this.accessCounter = 0;
    this.stats = { hits: 0, misses: 0 };
  }

  getStats(): CacheStats {
    const total = this.stats.hits + this.stats.misses;
    return {
      size: this.cache.size,
      hits: this.stats.hits,
      misses: this.stats.misses,
      hitRate: total > 0 ? this.stats.hits / total : 0,
    };
  }

  // Development helper for debugging
  printStats(): void {
    const stats = this.getStats();
    console.log("[RouteCache] Statistics:", {
      size: `${stats.size}/${this.maxSize}`,
      hitRate: `${(stats.hitRate * 100).toFixed(1)}%`,
      hits: stats.hits,
      misses: stats.misses,
    });
  }
}

// Request deduplication utilities
const pendingRequests = new Map<string, Promise<any>>();

export const deduplicateRequest = async <T>(
  key: string,
  requestFn: () => Promise<T>
): Promise<T> => {
  if (pendingRequests.has(key)) {
    console.log(
      "[RouteCache] Using deduplicated request:",
      key.substring(0, 20) + "..."
    );
    return pendingRequests.get(key)!;
  }

  const promise = requestFn();
  pendingRequests.set(key, promise);

  try {
    const result = await promise;
    return result;
  } finally {
    pendingRequests.delete(key);
  }
};

// Cleanup utility for pending requests (call on app shutdown or when needed)
export const clearPendingRequests = (): void => {
  pendingRequests.clear();
};

// Export global cache instance
export const routeCache = new RouteCache();

// Development helper - expose cache stats globally in dev mode
if (process.env.NODE_ENV === "development") {
  (window as any).routeCacheStats = () => routeCache.printStats();
}
